import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { useDebounce } from '@/hooks/useDebounce';
import { PageHint, IDocKey } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { FinanceLegacyEntryFormDialog } from '@components/finance-legacy/FinanceLegacyEntryFormDialog.tsx';
import {
  financeLegacyService,
  FinanceLegacyEntry,
  FinanceLegacyOptions,
  SOURCE_APP_LEGACY,
  SOURCE_APP_WEB,
} from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

// Експеримент (2026-08-21): та сама таблиця entries, що й в "Записи", але з
// фіксованим type - для табів "Витрати"/"Доходи". Таб "Записи"
// (FinanceLegacyEntriesTab.tsx) свідомо не чіпали за проханням користувача -
// він шукає рішення для нього окремо. Тому тут окремий, дещо дубльований
// компонент, а не спільний рефакторинг з "Записи".
//
// Write-доступ (2026-08-21): "Додати" створює запис з sourceApp=WEB.
// Редагування (олівець) - лише для власних (WEB) записів, легасі-записи
// редагувати з нашого боку не можна (бек кине 403).
const SourceAppChip: React.FC<{ sourceApp: string }> = ({ sourceApp }) => (
  sourceApp === SOURCE_APP_WEB
    ? <Chip label="Веб" color="primary" size="small" variant="outlined" />
    : <Chip label="Легасі" size="small" variant="outlined" />
);

export const FinanceLegacyEntriesByTypeTab: React.FC<{
  options: FinanceLegacyOptions;
  type: number;
  iDoc: IDocKey;
}> = ({ options, type, iDoc }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sourceAppFilter, setSourceAppFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [entries, setEntries] = useState<FinanceLegacyEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FinanceLegacyEntry | undefined>(undefined);
  const [deletingEntry, setDeletingEntry] = useState<FinanceLegacyEntry | undefined>(undefined);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const result = await financeLegacyService.getEntries({
        from: fromDate ? fromDate.toISOString() : undefined,
        to: toDate ? toDate.toISOString() : undefined,
        projectId: projectFilter ? Number(projectFilter) : undefined,
        sourceId: sourceFilter ? Number(sourceFilter) : undefined,
        sourceApp: sourceAppFilter || undefined,
        search: debouncedSearch || undefined,
        type,
        page,
        limit,
      });
      setEntries(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити записи.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, projectFilter, sourceFilter, sourceAppFilter, debouncedSearch, type, page, limit]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { setPage(1); }, [type]);

  const projectLabel = (id: number | null) => options.projects.find((p) => p.value === id)?.label ?? id ?? '—';
  const sourceLabel = (id: number | null) => options.sources.find((s) => s.value === id)?.label ?? id ?? '—';
  const currencyLabel = (id: number | null) => options.currencies.find((c) => c.value === id)?.label ?? id ?? '—';

  const openCreate = () => { setEditingEntry(undefined); setDialogOpen(true); };
  const openEdit = (entry: FinanceLegacyEntry) => { setEditingEntry(entry); setDialogOpen(true); };

  const confirmDelete = async () => {
    if (!deletingEntry) return;
    try {
      await financeLegacyService.deleteEntry(deletingEntry.id);
      toastService.success('Запис видалено.');
      fetchEntries();
    } catch {
      toastService.error('Не вдалося видалити запис.');
    } finally {
      setDeletingEntry(undefined);
    }
  };

  return (
    <Box data-i-doc={iDoc}>
      <PageHint iDoc={iDoc} />

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
        <PageToolbar
          storageKey={`filters:dev-finance-legacy-type-${type}`}
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук по коментарю',
              value: search,
              onChange: (v) => { setSearch(v); setPage(1); },
            },
            {
              id: 'project',
              type: 'select',
              label: 'Проєкт',
              value: projectFilter,
              onChange: (v) => { setProjectFilter(v); setPage(1); },
              options: [{ value: '', label: 'Всі' }, ...options.projects.map((p) => ({ value: String(p.value), label: p.label }))],
            },
            {
              id: 'source',
              type: 'select',
              label: 'Джерело',
              value: sourceFilter,
              onChange: (v) => { setSourceFilter(v); setPage(1); },
              options: [{ value: '', label: 'Всі' }, ...options.sources.map((s) => ({ value: String(s.value), label: s.label }))],
            },
            {
              id: 'sourceApp',
              type: 'select',
              label: 'Інтерфейс',
              value: sourceAppFilter,
              onChange: (v) => { setSourceAppFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: SOURCE_APP_LEGACY, label: 'Легасі' },
                { value: SOURCE_APP_WEB, label: 'Веб' },
              ],
            },
            {
              id: 'from',
              type: 'custom',
              value: fromDate,
              renderCustom: () => (
                <DatePicker
                  label="Від"
                  value={fromDate}
                  onChange={(v) => { setFromDate(v); setPage(1); }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              ),
            },
            {
              id: 'to',
              type: 'custom',
              value: toDate,
              renderCustom: () => (
                <DatePicker
                  label="До"
                  value={toDate}
                  onChange={(v) => { setToDate(v); setPage(1); }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              ),
            },
          ]}
          onReset={() => { setFromDate(null); setToDate(null); setProjectFilter(''); setSourceFilter(''); setSourceAppFilter(''); setSearch(''); setPage(1); }}
          showReset
          extraActions={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>Додати</Button>}
        />
      </LocalizationProvider>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Typography sx={{ mt: 4, textAlign: 'center' }}>Записів не знайдено.</Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                {!isMobile && <TableCell>Проєкт</TableCell>}
                {!isMobile && <TableCell>Джерело</TableCell>}
                <TableCell>Сума</TableCell>
                {!isMobile && <TableCell>Сума (USD)</TableCell>}
                {!isMobile && <TableCell>Коментар</TableCell>}
                {!isMobile && <TableCell>Співробітник (id)</TableCell>}
                {!isMobile && <TableCell>Інтерфейс</TableCell>}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    {new Date(entry.createdAt).toLocaleDateString('uk-UA')}
                    {isMobile && (
                      <Box sx={{ mt: 0.5 }}>
                        <SourceAppChip sourceApp={entry.sourceApp} />
                      </Box>
                    )}
                  </TableCell>
                  {!isMobile && <TableCell>{projectLabel(entry.projectId)}</TableCell>}
                  {!isMobile && <TableCell>{sourceLabel(entry.sourceId)}</TableCell>}
                  <TableCell>
                    {entry.amount ?? '—'} {currencyLabel(entry.currencyId)}
                    {isMobile && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {entry.amountUsd ?? '—'} USD
                      </Typography>
                    )}
                  </TableCell>
                  {!isMobile && <TableCell>{entry.amountUsd ?? '—'}</TableCell>}
                  {!isMobile && <TableCell>{entry.comment ?? '—'}</TableCell>}
                  {!isMobile && <TableCell>{entry.empId ?? '—'}</TableCell>}
                  {!isMobile && <TableCell><SourceAppChip sourceApp={entry.sourceApp} /></TableCell>}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {entry.sourceApp === SOURCE_APP_WEB && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(entry)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeletingEntry(entry)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey={`pagination:dev-finance-legacy-type-${type}`}
      />

      <FinanceLegacyEntryFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        type={type}
        options={options}
        entry={editingEntry}
        onSaved={fetchEntries}
      />

      <ConfirmationDialog
        open={!!deletingEntry}
        onClose={() => setDeletingEntry(undefined)}
        onConfirm={confirmDelete}
        title="Видалити запис?"
        message="Запис буде позначено як видалений (soft-delete). Дію можна скасувати лише вручну в БД."
        confirmText="Видалити"
      />
    </Box>
  );
};
